import { round } from '@/utils/mathUtils';
import type { ColumnDef } from '@tanstack/react-table';
import dayjs from 'dayjs';
import {
  Calendar,
  Database,
  DollarSign,
  FileX,
  Gift,
  Hash,
  Package2,
  Percent,
  Receipt,
  TrendingUp,
} from 'lucide-react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/UI/TableShadcn';
import { cn } from 'lib/utils';

import type { ICompanyItemInterface } from '../../api/CompanyItems.interface';

import ClientColumn from './ClientColumn';
import ItemColumn from './ItemColumn';

type Props = {
  items: ICompanyItemInterface[] | undefined;
};

function ItemsTable({ items }: Props) {
  const columns = useMemo<ColumnDef<ICompanyItemInterface>[]>(
    () => [
      {
        accessorKey: 'ID',
        header: () => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Hash className="h-3.5 w-3.5" />
            <span>#</span>
          </div>
        ),
        size: 60,
        cell: (row) => {
          return (
            <div className="flex items-center justify-center">
              <Badge variant="outline" className="h-6 w-8 font-mono text-xs">
                {row.row.index + 1}
              </Badge>
            </div>
          );
        },
      },
      {
        accessorKey: 'ItemName',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Package2 className="h-4 w-4" />
            <span>تفاصيل الصنف</span>
          </div>
        ),
        size: 280,
        cell: (row) => {
          return (
            <ItemColumn
              itemID={row?.row?.original?.ItemID}
              itemName={row?.row?.original?.ItemName}
            />
          );
        },
      },
      {
        accessorKey: 'ClientName',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Receipt className="h-4 w-4" />
            <span>بيانات العميل</span>
          </div>
        ),
        size: 280,
        cell: (row) => {
          return (
            <ClientColumn
              clientName={row.row.original.ClientName}
              storeID={row.row.original.storeID}
              clientID={row.row.original.ClientID}
            />
          );
        },
      },

      {
        accessorKey: 'UnitPrice',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <DollarSign className="h-4 w-4" />
            <span>السعر</span>
          </div>
        ),
        size: 120,
        cell: ({ row }) => {
          const price = round(row?.original?.UnitPrice, 2);
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-emerald-50 px-3 py-2 dark:bg-emerald-950/30">
                <span className="font-mono text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  {price}
                </span>
                <span className="mr-1 text-xs text-emerald-600 dark:text-emerald-500">
                  ج.م
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'TrDate',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Calendar className="h-4 w-4" />
            <span>التاريخ</span>
          </div>
        ),
        size: 140,
        cell: (row) => {
          const date = dayjs(new Date(row.row.original.TrDate));
          return (
            <div className="flex flex-col items-center gap-1">
              <div className="rounded-lg bg-slate-100 px-3 py-1.5 dark:bg-slate-800">
                <span className="font-mono text-sm font-medium text-slate-700 dark:text-slate-300">
                  {date.format('YYYY/MM/DD')}
                </span>
              </div>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {date.format('dddd')}
              </span>
            </div>
          );
        },
      },
      {
        accessorKey: 'Discount',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Percent className="h-4 w-4" />
            <span>الخصم</span>
          </div>
        ),
        size: 100,
        meta: { type: 'm' },
        cell: (row) => {
          const discount = round(row?.row?.original?.Discount || 0, 2);
          return (
            <div className="flex items-center justify-center">
              {discount > 0 ? (
                <div className="rounded-lg bg-orange-50 px-2 py-1.5 dark:bg-orange-950/30">
                  <span className="font-mono text-sm font-semibold text-orange-700 dark:text-orange-400">
                    {discount}%
                  </span>
                </div>
              ) : (
                <span className="text-sm text-slate-400">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'Qty',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Database className="h-4 w-4" />
            <span>الكمية</span>
          </div>
        ),
        size: 100,
        cell: (row) => {
          const qty = round(row?.row?.original?.Qty || 0, 2);
          return (
            <div className="flex items-center justify-center">
              <div className="rounded-lg bg-blue-50 px-3 py-1.5 dark:bg-blue-950/30">
                <span className="font-mono text-sm font-bold text-blue-700 dark:text-blue-400">
                  {qty || '0'}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'Bonus',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <Gift className="h-4 w-4" />
            <span>البونص</span>
          </div>
        ),
        size: 100,
        cell: (row) => {
          const bonus = round(row?.row?.original?.Bonus || 0, 2);
          return (
            <div className="flex items-center justify-center">
              {bonus > 0 ? (
                <div className="rounded-lg bg-purple-50 px-2 py-1.5 dark:bg-purple-950/30">
                  <span className="font-mono text-sm font-bold text-purple-700 dark:text-purple-400">
                    {bonus}
                  </span>
                </div>
              ) : (
                <span className="text-sm text-slate-400">-</span>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: 'type',
        header: ({ column: _column }) => (
          <div className="flex items-center gap-2 font-semibold text-slate-700 dark:text-slate-300">
            <TrendingUp className="h-4 w-4" />
            <span>نوع المعاملة</span>
          </div>
        ),
        size: 140,
        cell: (row) => {
          const type = row?.row?.original?.type?.trim();
          const getTypeConfig = (type: string) => {
            switch (type) {
              case 'بيع':
                return {
                  color:
                    'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800/50',
                  icon: '📈',
                };
              case 'شراء':
                return {
                  color:
                    'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800/50',
                  icon: '🛒',
                };
              case 'مرتجع عميل':
                return {
                  color:
                    'bg-red-100 text-red-800 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800/50',
                  icon: '↩️',
                };
              default:
                return {
                  color:
                    'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-950/30 dark:text-orange-400 dark:border-orange-800/50',
                  icon: '📋',
                };
            }
          };

          const config = getTypeConfig(type);

          return (
            <div className="flex items-center justify-center">
              <Badge
                variant="outline"
                className={cn('gap-2 px-3 py-1.5 font-medium', config.color)}
              >
                <span>{config.icon}</span>
                <span>{type || 'غير محدد'}</span>
              </Badge>
            </div>
          );
        },
      },
    ],
    [], // end
  );

  return (
    <div className="h-full w-full">
      {items && items.length > 0 ? (
        <div className="h-full overflow-hidden rounded-lg">
          <DataTable
            columns={columns}
            data={items}
            isPaginated={true}
            pageSize={20}
            isTopToolbar={true}
            isRowSelection={true}
            heightTable="calc(100vh - 280px)"
            visibleColumns={['Discount']}
            columnsSelectFilters={[
              {
                columnId: 'type',
                title: 'نوع المعاملة',
                options: [
                  { value: 'بيع', label: 'بيع' },
                  { value: 'شراء', label: 'شراء' },
                  { value: 'مرتجع عميل', label: 'مرتجع عميل' },
                ],
              },
            ]}
          />
        </div>
      ) : (
        <div className="flex h-full flex-col items-center justify-center">
          <div className="max-w-md space-y-6 text-center">
            {/* Modern Empty State Icon */}
            <div className="relative mx-auto w-fit">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="h-32 w-32 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 opacity-50 blur-xl dark:from-slate-700 dark:to-slate-800" />
              </div>
              <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 shadow-lg dark:from-slate-800 dark:to-slate-900">
                <FileX className="h-12 w-12 text-slate-400 dark:text-slate-500" />
              </div>
            </div>

            {/* Empty State Content */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                لا توجد بيانات
              </h3>
              <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                لم يتم العثور على أي حركات للشركة المختارة خلال الفترة الزمنية
                المحددة
              </p>
            </div>

            {/* Suggestions */}
            <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-800/50">
              <h4 className="mb-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                جرب التالي:
              </h4>
              <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
                <li>• تغيير الفترة الزمنية</li>
                <li>• اختيار شركة أخرى</li>
                <li>• مراجعة فلاتر البحث</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ItemsTable;
